import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteCampaign, getCampaigns } from '../../../../../api/endpoints/cmsContent';
import type { Campaign } from '../../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../../components/ListSearchField';
import { CampaignFormDialog } from '../CampaignFormDialog';

/** List rendered as a plain MUI Table (@mui/x-data-grid is banned under this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + full live curl round-trip
 *  (see api/types/cmsContent.ts header). This page is only ever rendered as a tab inside
 *  `SiteDetailPage` for one specific site, so `siteId` comes in as a fixed prop. There is no
 *  `GetCampaignById` route (confirmed) — the edit dialog is handed the row straight from this
 *  page's own query cache. */
export default function CampaignListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'content', 'campaigns', siteId],
    queryFn: () => getCampaigns(siteId),
  });
  const rows: Campaign[] = useMemo(() => query.data?.airCampaigns ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.airlineName, row.airlineCode, row.from, row.to, row.link]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCampaign(id),
    onSuccess: () => {
      enqueueSnackbar('Kampanya silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'campaigns'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Kampanyalar</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Kampanya
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Havayolu, kalkış, varış, link..." />
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Havayolu</TableCell>
              <TableCell>Kalkış</TableCell>
              <TableCell>Varış</TableCell>
              <TableCell>Fiyat</TableCell>
              <TableCell>Tarih Aralığı</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı kampanya bulunamadı.' : 'Aramayla eşleşen kampanya bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.airlineName ?? '—'} {row.airlineCode ? `(${row.airlineCode})` : ''}
                </TableCell>
                <TableCell>{row.from ?? '—'}</TableCell>
                <TableCell>{row.to ?? '—'}</TableCell>
                <TableCell>{row.price !== undefined ? `${row.price} ${row.priceCurrency ?? ''}`.trim() : '—'}</TableCell>
                <TableCell>
                  {row.startDate ? row.startDate.slice(0, 10) : '—'} — {row.endDate ? row.endDate.slice(0, 10) : '—'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CampaignFormDialog open={createOpen} campaign={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <CampaignFormDialog open={!!editing} campaign={editing} siteId={siteId} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Kampanyayı sil"
        description={`"${deleting?.airlineName ?? ''}" kampanyasını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
